import {
  Box, Typography, Card, CardContent, Chip, LinearProgress, Button,
} from "@mui/material";
import { AlertCircle, CheckCircle2, Download, FileText, Plus, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import ScoreRing from "./ScoreRing";
import UpgradeBanner from "./UpgradeBanner";
import CoverLetterModal from "./CoverLetterModal";
import DownloadOptionsModal from "./DownloadOptionsModal";

const scoreColor = (s) => s >= 70 ? "#10b981" : s >= 50 ? "#f59e0b" : "#ef4444";
const scoreLabel = (s) => s >= 70 ? "Great" : s >= 50 ? "Fair" : "Needs work";

const LOADING_STEPS = [
  { label: "Reading your resume…", pct: 15 },
  { label: "Matching job description keywords…", pct: 35 },
  { label: "Identifying skill gaps…", pct: 55 },
  { label: "Generating optimized resume…", pct: 75 },
  { label: "Calculating ATS score…", pct: 90 },
];

function AnalysisLoader() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setStep((s) => Math.min(s + 1, LOADING_STEPS.length - 1));
    }, 2800);
    return () => clearInterval(id);
  }, []);

  const { label, pct } = LOADING_STEPS[step];

  return (
    <Card sx={{ height: "100%", minHeight: 400 }}>
      <CardContent sx={{
        height: "100%", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", p: 6, textAlign: "center",
      }}>
        <Box sx={{
          width: 72, height: 72, borderRadius: "18px",
          background: "linear-gradient(135deg,#2563eb,#7c3aed)",
          display: "flex", alignItems: "center", justifyContent: "center", mb: 3,
          animation: "pulse 1.6s ease-in-out infinite",
          "@keyframes pulse": {
            "0%,100%": { transform: "scale(1)", opacity: 1 },
            "50%": { transform: "scale(1.08)", opacity: 0.85 },
          },
        }}>
          <FileText size={32} color="#fff" />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", mb: 0.5 }}>
          Analyzing your resume
        </Typography>
        <Typography variant="body2" sx={{ color: "#64748b", mb: 3, minHeight: 24, transition: "all 0.4s" }}>
          {label}
        </Typography>
        <Box sx={{ width: "100%", maxWidth: 320 }}>
          <LinearProgress
            variant="determinate" value={pct}
            sx={{
              height: 6, borderRadius: 3, bgcolor: "#f1f5f9",
              "& .MuiLinearProgress-bar": {
                background: "linear-gradient(90deg,#2563eb,#7c3aed)",
                borderRadius: 3,
                transition: "transform 0.8s ease",
              },
            }}
          />
          <Typography variant="caption" sx={{ color: "#94a3b8", mt: 1, display: "block" }}>
            This usually takes 15–30 seconds
          </Typography>
        </Box>
        {LOADING_STEPS.map((s, i) => (
          <Box key={i} sx={{
            display: "flex", alignItems: "center", gap: 1.5, mt: i === 0 ? 3 : 1,
            opacity: i <= step ? 1 : 0.3, transition: "opacity 0.4s",
          }}>
            <Box sx={{
              width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
              bgcolor: i < step ? "#10b981" : i === step ? "#2563eb" : "#e2e8f0",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "background-color 0.4s",
            }}>
              {i < step && <CheckCircle2 size={12} color="#fff" />}
            </Box>
            <Typography variant="caption" sx={{
              color: i < step ? "#10b981" : i === step ? "#2563eb" : "#94a3b8",
              fontWeight: i === step ? 700 : 400, transition: "color 0.4s",
            }}>
              {s.label}
            </Typography>
          </Box>
        ))}
      </CardContent>
    </Card>
  );
}

export default function AnalysisResults({ analysis, isPaid, loading, onDownloadResume, onDownloadCoverLetter, downloadLoading, companyName = "" }) {
  const navigate = useNavigate();
  const score = analysis?.analysis?.atsScore ?? 0;
  const scoreBefore = analysis?.analysis?.atsScoreBefore ?? null;
  const [clModalOpen, setClModalOpen] = useState(false);
  const [dlModalOpen, setDlModalOpen] = useState(false);
  const [selectedKeywords, setSelectedKeywords] = useState(new Set());

  // Reset keyword selection when a new analysis comes in
  useEffect(() => {
    setSelectedKeywords(new Set());
  }, [analysis]);

  const toggleKeyword = (kw) => {
    setSelectedKeywords((prev) => {
      const next = new Set(prev);
      next.has(kw) ? next.delete(kw) : next.add(kw);
      return next;
    });
  };

  const selectedArr = Array.from(selectedKeywords);

  // ATS score is always free.
  // Keywords + resume + downloads: shown for credits > 1 (paid) or unlimited (-1).
  const credits = analysis?.credits ?? 0;
  const showKeywords = credits === -1 || credits > 1;

  if (loading) return <AnalysisLoader />;

  if (!analysis) {
    return (
      <Box>
        {/* Hero empty state */}
        <Card sx={{ mb: 2.5, background: "linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%)", border: "1px solid #e0e7ff" }}>
          <CardContent sx={{ p: 4, textAlign: "center" }}>
            <Box sx={{
              width: 72, height: 72, borderRadius: "18px",
              background: "linear-gradient(135deg,#2563eb,#7c3aed)",
              display: "flex", alignItems: "center", justifyContent: "center",
              mx: "auto", mb: 2.5,
            }}>
              <FileText size={32} color="#fff" />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", mb: 1 }}>
              Beat the ATS, land more interviews
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", maxWidth: 360, mx: "auto", lineHeight: 1.7 }}>
              Upload your resume and paste a job description. Our AI scores your resume against the job, fills keyword gaps, and generates a tailored cover letter — in under 30 seconds.
            </Typography>
          </CardContent>
        </Card>

        {/* How it works */}
        <Card sx={{ mb: 2.5 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="overline" sx={{ color: "#64748b", fontWeight: 700, letterSpacing: 1, display: "block", mb: 2 }}>
              HOW IT WORKS
            </Typography>
            {[
              { step: "01", title: "Upload your resume", desc: "PDF or DOCX — we parse every bullet point and skill.", color: "#2563eb", bg: "#eff6ff" },
              { step: "02", title: "Paste the job description", desc: "We extract the exact keywords recruiters and ATS systems look for.", color: "#7c3aed", bg: "#f5f3ff" },
              { step: "03", title: "Get your ATS score + optimized resume", desc: "See your score, missing keywords, and a rewritten resume ready to download.", color: "#10b981", bg: "#f0fdf4" },
            ].map(({ step, title, desc, color, bg }) => (
              <Box key={step} sx={{ display: "flex", gap: 2, mb: 2, "&:last-child": { mb: 0 } }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: "10px", bgcolor: bg, flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 800, color }}>{step}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a", mb: 0.25 }}>{title}</Typography>
                  <Typography variant="caption" sx={{ color: "#64748b", lineHeight: 1.5 }}>{desc}</Typography>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>

        {/* Feature highlights */}
        <Card sx={{ mb: 2.5 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="overline" sx={{ color: "#64748b", fontWeight: 700, letterSpacing: 1, display: "block", mb: 2 }}>
              WHAT YOU GET
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
              {[
                { icon: "📊", label: "ATS Score /100", desc: "Know exactly where you stand" },
                { icon: "🔑", label: "Missing Keywords", desc: "Add what recruiters look for" },
                { icon: "✨", label: "Optimized Resume", desc: "AI-rewritten bullet points" },
                { icon: "📝", label: "Cover Letter", desc: "Tailored to the role in seconds" },
              ].map(({ icon, label, desc }) => (
                <Box key={label} sx={{ p: 1.75, borderRadius: "10px", bgcolor: "#f8fafc", border: "1px solid #f1f5f9" }}>
                  <Typography sx={{ fontSize: 20, mb: 0.5 }}>{icon}</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#0f172a", display: "block" }}>{label}</Typography>
                  <Typography variant="caption" sx={{ color: "#94a3b8", lineHeight: 1.4 }}>{desc}</Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Social proof */}
        <Card sx={{ background: "linear-gradient(135deg,#0f172a,#1e293b)", border: "none" }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body2" sx={{ color: "#94a3b8", fontStyle: "italic", lineHeight: 1.7, mb: 1.5 }}>
              "I went from 38/100 to 84/100 after optimizing with Hyrvix. Got a callback from the exact company I applied to within a week."
            </Typography>
            <Typography variant="caption" sx={{ color: "#475569", fontWeight: 700 }}>
              — Software Engineer, 3 YOE
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box className="fade-in-up">
      {/* ATS Score */}
      <Card sx={{ mb: 2.5, overflow: "hidden" }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
              <Typography variant="overline" sx={{ color: "#64748b", fontWeight: 700, letterSpacing: 1 }}>
                ATS SCORE
              </Typography>
              {scoreBefore != null ? (
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 0.5 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: "#94a3b8", lineHeight: 1.1, textDecoration: "line-through" }}>
                    {scoreBefore}
                  </Typography>
                  <Typography variant="h6" sx={{ color: "#94a3b8" }}>→</Typography>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: scoreColor(score), lineHeight: 1.1 }}>
                    {score}
                    <Typography component="span" variant="h5" sx={{ color: "#cbd5e1", fontWeight: 400 }}>/100</Typography>
                  </Typography>
                </Box>
              ) : (
                <Typography variant="h3" sx={{ fontWeight: 800, color: scoreColor(score), lineHeight: 1.1, mt: 0.5 }}>
                  {score}
                  <Typography component="span" variant="h5" sx={{ color: "#cbd5e1", fontWeight: 400 }}>/100</Typography>
                </Typography>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
                <Chip
                  label={scoreLabel(score)} size="small"
                  sx={{
                    fontWeight: 700, fontSize: 11,
                    bgcolor: score >= 70 ? "#dcfce7" : score >= 50 ? "#fef9c3" : "#fee2e2",
                    color: scoreColor(score),
                  }}
                />
                {scoreBefore != null && (
                  <Typography variant="caption" sx={{ color: "#10b981", fontWeight: 700 }}>
                    +{score - scoreBefore} pts improved
                  </Typography>
                )}
              </Box>
            </Box>
            <ScoreRing score={score} />
          </Box>
          <LinearProgress
            variant="determinate" value={score}
            sx={{
              mt: 2.5, height: 6, borderRadius: 3, bgcolor: "#f1f5f9",
              "& .MuiLinearProgress-bar": { bgcolor: scoreColor(score), borderRadius: 3 },
            }}
          />
          {analysis.isCached && (
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: "8px", bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", display: "flex", alignItems: "flex-start", gap: 1 }}>
              <CheckCircle2 size={14} color="#16a34a" style={{ marginTop: 2, flexShrink: 0 }} />
              <Box>
                <Typography variant="caption" sx={{ color: "#15803d", fontWeight: 700, display: "block", lineHeight: 1.4 }}>
                  Score verified — same resume &amp; job description
                </Typography>
                <Typography variant="caption" sx={{ color: "#166534", display: "block", lineHeight: 1.5, mt: 0.25 }}>
                  We detected you've analyzed this exact resume + job description before. Your score is consistent and accurate — no re-processing needed.
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>


      {/* Summary */}
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="overline" sx={{ color: "#64748b", fontWeight: 700, letterSpacing: 1 }}>
            SUMMARY
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: "#334155", lineHeight: 1.7 }}>
            {analysis.analysis.summary}
          </Typography>
        </CardContent>
      </Card>

      {/* Missing Keywords — visible for credits > 1 or unlimited */}
      {showKeywords ? (
        <Card sx={{ mb: 2.5 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AlertCircle size={16} color="#ef4444" />
                <Typography variant="overline" sx={{ color: "#64748b", fontWeight: 700, letterSpacing: 1 }}>
                  MISSING KEYWORDS
                </Typography>
              </Box>
              {selectedArr.length > 0 && (
                <Typography variant="caption" sx={{ color: "#2563eb", fontWeight: 700 }}>
                  {selectedArr.length} selected · will be added to download
                </Typography>
              )}
            </Box>
            <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", mb: 1.5 }}>
              Click keywords to include them in your resume &amp; cover letter download
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {analysis.analysis.missingKeywords?.map((kw, i) => {
                const selected = selectedKeywords.has(kw);
                return (
                  <Chip
                    key={i}
                    label={kw}
                    size="small"
                    onClick={() => toggleKeyword(kw)}
                    icon={selected
                      ? <Check size={11} style={{ color: "#fff", marginLeft: 6 }} />
                      : <Plus size={11} style={{ color: "#b91c1c", marginLeft: 6 }} />}
                    sx={{
                      cursor: "pointer",
                      fontWeight: 700, fontSize: 12,
                      transition: "all 0.15s",
                      ...(selected
                        ? { bgcolor: "#b91c1c", color: "#fff", border: "1px solid #b91c1c", "& .MuiChip-icon": { color: "#fff" } }
                        : { bgcolor: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca", "&:hover": { bgcolor: "#fee2e2" } }
                      ),
                    }}
                  />
                );
              })}
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ mb: 2.5 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <AlertCircle size={16} color="#94a3b8" />
              <Typography variant="overline" sx={{ color: "#64748b", fontWeight: 700, letterSpacing: 1 }}>
                MISSING KEYWORDS
              </Typography>
            </Box>
            <Box sx={{ p: 2, borderRadius: "10px", bgcolor: "#f8fafc", border: "1px solid #e2e8f0", textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "#64748b", mb: 1 }}>
                Keyword analysis is available with more credits.
              </Typography>
              <Button size="small" variant="contained" onClick={() => navigate("/pricing")}
                sx={{ borderRadius: "8px", fontWeight: 700, fontSize: 12, background: "linear-gradient(135deg,#2563eb,#7c3aed)" }}>
                Get Credits →
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Optimization Summary */}
      {analysis.analysis.optimizationSummary?.length > 0 && (
        <Card sx={{ mb: 2.5 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <CheckCircle2 size={16} color="#10b981" />
              <Typography variant="overline" sx={{ color: "#64748b", fontWeight: 700, letterSpacing: 1 }}>
                WHAT WAS OPTIMIZED
              </Typography>
            </Box>
            {analysis.analysis.optimizationSummary.map((item, i) => (
              <Box key={i} sx={{ display: "flex", gap: 1.5, mb: 1.5, alignItems: "flex-start", "&:last-child": { mb: 0 } }}>
                <Box sx={{ width: 20, height: 20, borderRadius: "50%", bgcolor: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: 0.15 }}>
                  <CheckCircle2 size={12} color="#16a34a" />
                </Box>
                <Typography variant="body2" sx={{ color: "#334155", lineHeight: 1.6 }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Optimized Resume Preview */}
      <Card sx={{ mb: 2.5, overflow: "hidden", position: "relative" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="overline" sx={{ color: "#64748b", fontWeight: 700, letterSpacing: 1 }}>
            OPTIMIZED RESUME PREVIEW
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 1.5, whiteSpace: "pre-wrap", lineHeight: 1.7, color: "#334155",
              maxHeight: isPaid ? 260 : 100,
              overflow: "hidden",
              filter: isPaid ? "none" : "blur(4px)",
              userSelect: isPaid ? "text" : "none",
              fontSize: 12,
            }}
          >
            {analysis.optimizedText}
          </Typography>
          {!isPaid && (
            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(248,250,252,0.6)", backdropFilter: "blur(2px)" }}>
              <Button variant="contained" size="small" onClick={() => navigate("/pricing")}
                sx={{ borderRadius: "8px", fontWeight: 700, background: "linear-gradient(135deg,#2563eb,#7c3aed)" }}>
                Unlock Full Resume →
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      {isPaid ? (
        <Box>
          {downloadLoading && (
            <LinearProgress sx={{ mb: 1.5, borderRadius: 3, height: 4, bgcolor: "#f1f5f9", "& .MuiLinearProgress-bar": { background: "linear-gradient(90deg,#2563eb,#7c3aed)", borderRadius: 3 } }} />
          )}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button fullWidth variant="contained" startIcon={<Download size={16} />}
              onClick={() => setDlModalOpen(true)} disabled={downloadLoading}
              sx={{ py: 1.5, borderRadius: "10px", fontWeight: 700, background: "linear-gradient(135deg,#2563eb,#7c3aed)", "&:hover": { boxShadow: "0 6px 20px rgba(37,99,235,0.4)" }, "&:disabled": { opacity: 0.5 } }}
            >
              {downloadLoading ? "Generating…" : "Download Resume PDF"}
            </Button>
            <Button fullWidth variant="outlined" startIcon={<FileText size={16} />}
              onClick={() => setClModalOpen(true)} disabled={downloadLoading}
              sx={{ py: 1.5, borderRadius: "10px", fontWeight: 700, borderColor: "#e2e8f0", color: "#0f172a", "&:hover": { borderColor: "#2563eb", bgcolor: "#eff6ff" }, "&:disabled": { opacity: 0.5 } }}
            >
              {downloadLoading ? "Generating…" : "Cover Letter"}
            </Button>
          </Box>
        </Box>
      ) : (
        <UpgradeBanner />
      )}

      <DownloadOptionsModal
        open={dlModalOpen}
        onClose={() => setDlModalOpen(false)}
        loading={downloadLoading}
        onDownload={(template, color) => {
          setDlModalOpen(false);
          onDownloadResume(selectedArr, template, color);
        }}
      />

      <CoverLetterModal
        open={clModalOpen}
        onClose={() => setClModalOpen(false)}
        loading={downloadLoading}
        defaultCompanyName={companyName}
        onSubmit={({ companyName: cn, hiringManagerName }) => {
          setClModalOpen(false);
          onDownloadCoverLetter({ companyName: cn, hiringManagerName, selectedKeywords: selectedArr });
        }}
      />
    </Box>
  );
}
